import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import { getToken } from 'src/utils/auth';
import { postAPI } from 'src/utils/api';
import { useNotify } from 'src/context/ContextNotify';
import { isEmpty } from 'src/common/helper';

const rowStyle = {
  '&:last-child td, &:last-child th': { border: 0 },
  '&.MuiTableRow-root .MuiTableCell-root.MuiTableCell-body': {
    py: '8px',
    borderBottom: '1px solid #F3F3F3',
  },
};

const inputStyle = {
  '& .MuiInputBase-input.MuiOutlinedInput-input': { p: '8px' },
  '&.MuiFormControl-root.MuiTextField-root': { mt: '4px' },
  '&.MuiFormControl-root.MuiTextField-root .MuiInputBase-root.MuiOutlinedInput-root':
    { p: '4px' },
};

type DataType = {
  patientno: number | null;
  commonid: string | null;
  salon_no: string | null;
  fullname: string | null;
  kana: string | null;
  oldkana: string | null;
  oldname: string | null;
  gender: number | null;
  gender2: number | null;
  dob: Dayjs | null;
  phone1: string | null;
  phone2: string | null;
  email: string[];
  postcode: string | null;
  prefecture: number | null;
  address: string | null;
  note: string | null;
  vip: boolean;
};

const defaultError = {
  fullname: true,
  kana: true,
  gender: true,
  dob: true,
};

const Edit = (props: any) => {
  const navigate = useNavigate();
  const notifyContext = useNotify();
  const propsPatient = props.data?.patient || null;
  const [disablePatientno, setDisablePatientno] = useState(true);
  const [showOldname, setShowOldname] = useState(false);
  const [showOldkana, setShowOldkana] = useState(false);
  const [errors, setErrors] = useState(defaultError);
  const [patient, setPatient] = useState<DataType>({
    patientno: null,
    commonid: null,
    salon_no: null,
    fullname: null,
    kana: null,
    oldkana: null,
    oldname: null,
    gender: null,
    gender2: null,
    dob: null,
    phone1: null,
    phone2: null,
    email: [],
    postcode: null,
    prefecture: null,
    address: null,
    note: null,
    vip: false,
  });

  useEffect(() => {
    setPatient({
      patientno: propsPatient?.patientno || null,
      commonid: propsPatient?.commonid || null,
      salon_no: propsPatient?.salon_no || null,
      fullname: propsPatient?.fullname || null,
      kana: propsPatient?.kana || null,
      oldkana: propsPatient?.oldkana || null,
      oldname: propsPatient?.oldname || null,
      gender: propsPatient?.gender || 2,
      gender2: propsPatient?.gender2 || 4,
      dob: propsPatient?.dob
        ? dayjs(new Date(propsPatient?.dob))
        : dayjs(new Date()),
      phone1: propsPatient?.phone1 || null,
      phone2: propsPatient?.phone2 || null,
      email: propsPatient?.email.split(',') || [],
      postcode: propsPatient?.postcode || null,
      prefecture: propsPatient?.prefecture || null,
      address: propsPatient?.address || null,
      note: propsPatient?.note || null,
      vip: propsPatient?.vip || false,
    });
  }, [propsPatient]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setPatient({
      ...patient,
      [name]: value,
    });
  };

  const handleSelectChange = (event: SelectChangeEvent<number | string>) => {
    const { name, value } = event.target;
    setPatient({
      ...patient,
      [name]: value,
    });
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setPatient({
      ...patient,
      gender: Number(value),
    });
  };

  const handleDateChange = (newValue: dayjs.Dayjs | null) => {
    setPatient({
      ...patient,
      dob: newValue,
    });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPatient({
      ...patient,
      vip: event.target.checked,
    });
  };

  const handleDisablePatientnoChange = () => {
    setDisablePatientno(!disablePatientno);
  };

  const handleAddEmail = () => {
    const modifiedData = [...patient.email, ''];

    setPatient({
      ...patient,
      email: modifiedData,
    });
  };

  const handleChangeEmail = (event: any, index: number) => {
    const temp = [...patient.email];
    temp[index] = event.target.value;

    setPatient({
      ...patient,
      email: temp,
    });
  };

  const handleDeleteEmailClick = (index: number) => {
    let modifiedData = [...patient.email];
    modifiedData.splice(index, 1);
    setPatient((prevState) => ({
      ...prevState,
      email: modifiedData,
    }));
  };

  const handleShowOldname = () => {
    setShowOldname(!showOldname)
  }

  const handleShowOldkana = () => {
    setShowOldkana(!showOldkana)
  }

  const handleCancel = () => {
    navigate(`/patients`);
  };

  const validate = () => {
    if (isEmpty(patient.fullname)) {
      setErrors({ ...defaultError, fullname: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '名前を入力してください',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(patient.kana)) {
      setErrors({ ...defaultError, kana: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: 'カナを入力してください',
        type: 'error',
      });
      return false;
    }

    setErrors(defaultError);

    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error',
    });

    return true;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }
    const dataForm = {
      ...propsPatient,
      ...patient,
      dob: patient.dob ? patient.dob.format('YYYY/MM/DD') : null,
      gender2: patient.gender === 3 ? patient.gender2 : null,
      email: patient.email.join(','),
    };

    try {
      const token = getToken();
      postAPI(`/master/patient-save?token=${token}`, { ...dataForm }).then(
        (res) => {
          if (res && res.code === 200) {
            notifyContext.setNotifyMessage({
              show: true,
              message: '患者を変更しました',
              type: 'success',
            });
            navigate(`/patients`);
          }
          if (res.code === 500 || res.code === 501) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg || 'Failed to post data',
              type: 'error',
            });
          }
        }
      );
    } catch (error) {
      console.error('Failed to post data:', error);
      return;
    }
  };

  return (
    <>
      <Stack>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            width: '100%',
            height: '100%',
          }}
        >
          <Table
            sx={{
              minWidth: 470,
              '& .MuiTableCell-root.MuiTableCell-head': {
                borderBottom: '1px solid #F3F3F3',
                fontWeight: 700,
                p: '6px 16px',
                fontSize: '14px',
              },
              '& .MuiTableCell-root.MuiTableCell-body': {
                color: 'primary.dark',
                fontSize: '14px',
                py: '6px',
              },
            }}
            aria-label='simple table'
          >
            <TableBody sx={{ '&.MuiTableBody-root th': { fontWeight: 700 } }}>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>患者ID</TableCell>
                <TableCell>
                  <Stack direction={'row'} spacing={'8px'}>
                    <TextField
                      margin='dense'
                      id='patientno'
                      name='patientno'
                      value={patient.patientno}
                      onChange={(event) => handleChange(event)}
                      sx={inputStyle}
                      disabled={disablePatientno}
                    />
                    {!propsPatient?.id && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!disablePatientno}
                            onChange={handleDisablePatientnoChange}
                          />
                        }
                        label={'手動'}
                        sx={{
                          '& .MuiTypography-root': {
                            fontSize: '14px',
                            wordBreak: 'break-word',
                          },
                          mr: '0px',
                        }}
                      />
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={patient.vip}
                          onChange={(e) => handleCheckboxChange(e)}
                        />
                      }
                      label={'VIPフラグ'}
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '14px',
                          wordBreak: 'break-word',
                        },
                        mr: '0px',
                      }}
                    />
                  </Stack>
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>共通ID</TableCell>
                <TableCell>
                  <TextField
                    margin='dense'
                    id='commonid'
                    name='commonid'
                    value={patient.commonid}
                    onChange={(event) => handleChange(event)}
                    sx={inputStyle}
                  />
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>サロン番号</TableCell>
                <TableCell>
                  <TextField
                    margin='dense'
                    id='salon_no'
                    name='salon_no'
                    value={patient.salon_no}
                    onChange={(event) => handleChange(event)}
                    sx={inputStyle}
                  />
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>
                  <Stack direction={'row'}>
                    名前<Typography color={'#ED5565'}>*</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction={'row'}>
                    <TextField
                      error={!errors.fullname}
                      margin='dense'
                      id='fullname'
                      name='fullname'
                      value={patient.fullname}
                      onChange={(event) => handleChange(event)}
                      sx={inputStyle}
                    />
                    <Typography
                      sx={{ cursor: 'pointer', lineHeight: '50px', px: '4px' }}
                      onClick={handleShowOldname}
                    >
                      &gt;&gt;
                    </Typography>
                    {showOldname && (
                      <TextField
                        margin='dense'
                        id='oldname'
                        name='oldname'
                        value={patient.oldname}
                        onChange={(event) => handleChange(event)}
                        sx={inputStyle}
                      />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>
                  <Stack direction={'row'}>
                    カナ<Typography color={'#ED5565'}>*</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction={'row'}>
                    <TextField
                      error={!errors.kana}
                      margin='dense'
                      id='kana'
                      name='kana'
                      value={patient.kana}
                      onChange={(event) => handleChange(event)}
                      sx={inputStyle}
                    />
                    <Typography
                      sx={{ cursor: 'pointer', lineHeight: '50px', px: '4px' }}
                      onClick={handleShowOldkana}
                    >
                      &gt;&gt;
                    </Typography>
                    {showOldkana && (
                      <TextField
                        margin='dense'
                        id='oldkana'
                        name='oldkana'
                        value={patient.oldkana}
                        onChange={(event) => handleChange(event)}
                        sx={inputStyle}
                      />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>
                  <Stack direction={'row'}>
                    性別<Typography color={'#ED5565'}>*</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <RadioGroup
                    row
                    aria-labelledby='demo-controlled-radio-buttons-group'
                    name='gender'
                    value={patient.gender || 1}
                    onChange={handleRadioChange}
                  >
                    {props.data &&
                      props.data.genders.map((type: any, idx: number) => {
                        return (
                          <FormControlLabel
                            key={idx}
                            value={type.value}
                            control={<Radio />}
                            label={type.label}
                          />
                        );
                      })}
                  </RadioGroup>
                  {patient.gender === 3 && (
                    <Select
                      className='select-status'
                      value={patient.gender2 || ''}
                      name='gender2'
                      onChange={(event) => handleSelectChange(event)}
                      sx={{
                        '& .MuiSelect-select.MuiSelect-outlined': {
                          py: '5px',
                        },
                        width: '100%',
                      }}
                    >
                      {props.data &&
                        props.data.genders2.map((gen: any, index: number) => {
                          return (
                            <MenuItem key={index} value={gen.value}>
                              {gen.label}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  )}
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>
                  <Stack direction={'row'}>
                    生年月日<Typography color={'#ED5565'}>*</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      name=''
                      value={patient.dob}
                      onChange={(newValue) => handleDateChange(newValue)}
                      format='YYYY/MM/DD'
                      sx={{
                        width: '100%',
                        height: '32px',
                        my: '8px',
                        '& .MuiInputBase-input.MuiOutlinedInput-input': {
                          py: '8px',
                          fontSize: '14px',
                        },
                      }}
                    />
                  </LocalizationProvider>
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>電話番号</TableCell>
                <TableCell>
                  <TextField
                    margin='dense'
                    id='phone1'
                    name='phone1'
                    value={patient.phone1}
                    onChange={(event) => handleChange(event)}
                    sx={inputStyle}
                  />
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>電話番号2</TableCell>
                <TableCell>
                  <TextField
                    margin='dense'
                    id='phone2'
                    name='phone2'
                    value={patient.phone2}
                    onChange={(event) => handleChange(event)}
                    sx={inputStyle}
                  />
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>メールアドレス</TableCell>
                <TableCell>
                  <Stack spacing={'8px'}>
                    {patient.email &&
                      Array.isArray(patient.email) &&
                      patient.email.map((email: string, index: number) => {
                        return (
                          <Stack
                            key={index}
                            direction={'row'}
                            justifyContent='left'
                            spacing={'8px'}
                            sx={{
                              '& .MuiFormControl-root.MuiTextField-root .MuiOutlinedInput-root':
                                {
                                  width: '116px',
                                },
                            }}
                          >
                            <Stack
                              sx={{
                                '& .MuiFormControl-root.MuiTextField-root .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                                  {
                                    borderColor: !true
                                      ? '#d32f2f !important'
                                      : '',
                                  },
                              }}
                            >
                              <TextField
                                margin='dense'
                                value={email}
                                onChange={(event) =>
                                  handleChangeEmail(event, index)
                                }
                                sx={inputStyle}
                              />
                            </Stack>
                            <Box
                              component={'img'}
                              src='/images/icons/ic-trash.svg'
                              alt='trash'
                              sx={{ cursor: 'pointer' }}
                              onClick={() => handleDeleteEmailClick(index)}
                            />
                          </Stack>
                        );
                      })}
                    <BtnOutlineGradient
                      text='+'
                      handleButtonClick={handleAddEmail}
                    />
                  </Stack>
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>郵便番号</TableCell>
                <TableCell>
                  <TextField
                    margin='dense'
                    id='postcode'
                    name='postcode'
                    value={patient.postcode}
                    onChange={(event) => handleChange(event)}
                    sx={inputStyle}
                  />
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>都道府県</TableCell>
                <TableCell>
                  <Select
                    className='select-status'
                    value={patient.prefecture || ''}
                    name='prefecture'
                    onChange={(event) => handleSelectChange(event)}
                    sx={{
                      '& .MuiSelect-select.MuiSelect-outlined': {
                        py: '5px',
                      },
                      width: '100%',
                    }}
                  >
                    {props.data &&
                      props.data.prefectures.map(
                        (prefecture: any, index: number) => {
                          return (
                            <MenuItem key={index} value={prefecture.id}>
                              {prefecture.prefecture}
                            </MenuItem>
                          );
                        }
                      )}
                  </Select>
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>住所</TableCell>
                <TableCell>
                  <TextField
                    margin='dense'
                    id='address'
                    name='address'
                    value={patient.address}
                    onChange={(event) => handleChange(event)}
                    sx={inputStyle}
                  />
                </TableCell>
              </TableRow>
              <TableRow sx={rowStyle}>
                <TableCell component={'th'}>備考</TableCell>
                <TableCell>
                  <TextField
                    margin='dense'
                    id='note'
                    name='note'
                    value={patient.note}
                    fullWidth
                    multiline
                    rows={4}
                    onChange={(event) => handleChange(event)}
                    sx={inputStyle}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction={'row'} spacing={'20px'} mt={'32px'}>
          <BtnGradient text='登録' handleButtonClick={handleSave} />
          <BtnOutlineGradient
            text='キャンセル'
            handleButtonClick={handleCancel}
          />
        </Stack>
      </Stack>
    </>
  );
};

export default Edit;
